/** @jsxImportSource @emotion/react */

import { COLORS } from '@btime/constants'
import { css } from '@emotion/react'
import { last, prop } from 'ramda'
import { useTranslation } from 'react-i18next'
import React from 'react'
import SignaturePad from 'react-signature-pad-wrapper'

import { CnpjOrCpfInput } from './MaskedInput'
import { Label, SmallText, Text } from '../presentation/Text'
import { dataURLtoFile, useUploadRequest } from '../../utils/hooks/uploadV2'
import Button from '../actions/Button'
import FlexGrid, { FlexCol } from '../structure/FlexGrid'
import Form from './Form'
import IconText from '../presentation/IconText'
import Image from '../presentation/Image'
import Input from './Input'
import Link from '../actions/Link'
import Loading from '../helpers/Loading'
import Modal from '../structure/Modal'
import Separator from '../helpers/Separator'

// TODO: Refactor
const SignatureControls = ({ pad, isEditing, onStartEditing }) => {
  const { t } = useTranslation('components')
  const style = css`
    position: absolute;
    left: 0.5rem;
    bottom: 0.3rem;
    right: 0.5rem;
  `

  const onClear = () => {
    if (!!pad) pad.clear()
    onStartEditing()
  }

  return (
    <div css={style} className="controls">
      <FlexGrid justify="space-between" gutter={0.5}>
        <Link onClick={onClear} bgColor={COLORS.transparent} color={COLORS.dark3} borderColor={COLORS.dark3}>
          <IconText
            color={COLORS.red}
            label={!isEditing ? t('clearAndInsertNewSignature') : t('clear')}
            iconName="BsTrash"
            iconSize=".7rem"
            size="small"
            left
          />
        </Link>
      </FlexGrid>
    </div>
  )
}

// TODO: Refactor
const SignatureInput = ({ value, onChange, requiredDoc, profileOnly, useSavedUserSignature }) => {
  const { t } = useTranslation(['components', 'common'])
  const { file, name: initialName, document: initialDoc, src: valueSrc } = value || {}
  const image = valueSrc ?? prop('src', file)
  const [isEditing, setEditing] = React.useState(false)
  const [hasSignature, setHasSignature] = React.useState(false)
  const [name, setName] = React.useState(initialName || '')
  const [document, setDoc] = React.useState(initialDoc || '')

  const nameRef = React.useRef(initialName || '')
  const documentRef = React.useRef(initialDoc || '')

  React.useEffect(() => {
    if (!isEditing) {
      setName(initialName || '')
      setDoc(initialDoc || '')
    }
  }, [initialName, initialDoc, isEditing])

  const wrapperRef = React.useRef(null)
  const signRef = React.useRef(null)
  const onStartEditing = () => {
    setEditing(true)
    setHasSignature(false)
    const nameVal = initialName || ''
    const docVal = initialDoc || ''
    setName(nameVal)
    setDoc(docVal)
    nameRef.current = nameVal
    documentRef.current = docVal
  }

  const setNameAndRef = React.useCallback(v => {
    setName(v)
    nameRef.current = v
  }, [])
  const setDocAndRef = React.useCallback(v => {
    setDoc(v)
    documentRef.current = v
  }, [])

  const effectiveName = isEditing ? (nameRef.current !== undefined ? nameRef.current : name) : name
  const effectiveDoc = isEditing ? (documentRef.current !== undefined ? documentRef.current : document) : document
  const isValid = profileOnly ? !!hasSignature : !!hasSignature && !!effectiveName && (!requiredDoc || !!effectiveDoc)

  const onBegin = React.useCallback(() => {
    setHasSignature(true)
  }, [])

  const handleChange = React.useCallback(
    customFile => {
      const doc = customFile || file
      const id = prop('id', doc)
      const src = prop('src', doc)
      const currentName = nameRef.current !== undefined ? nameRef.current : name
      const currentDoc = documentRef.current !== undefined ? documentRef.current : document
      const newValue = profileOnly
        ? { documentId: id, src: src ?? value?.src }
        : { name: currentName, file: doc, documentId: id, ...(currentDoc && { document: currentDoc }) }
      onChange && onChange(newValue)
    },
    [file, name, document, onChange, profileOnly, value?.src]
  )

  const [upload, { loading }] = useUploadRequest(docs => handleChange(last(docs)))

  const confirmSignature = e => {
    e?.preventDefault?.()
    if (signRef.current) {
      const pad = signRef.current
      if (!pad.isEmpty()) {
        dataURLtoFile(pad.toDataURL(), 'assinatura.png', 'image/png') //
          .then(data => upload([data]))
      }
    }
    setEditing(false)
  }

  const style = css`
    & > .padWidthSignatureWrapper,
    & > .emptyPadWrapper,
    & .padWrapper {
      border: solid 2px ${COLORS.light3};
      border-style: dotted;
      border-radius: 5px;
    }

    & > .emptyPadWrapper,
    & > .padWidthSignatureWrapper,
    & .padWrapper,
    & > .userData {
      max-width: 100%;
    }

    & > .emptyPadWrapper,
    & > .padWidthSignatureWrapper {
      min-height: 194px;
    }

    & > .emptyPadWrapper {
      background: ${COLORS.light1};
      display: flex;
      align-items: center;
      justify-content: center;
    }

    & .padWrapper,
    & > .padWidthSignatureWrapper {
      position: relative;
    }
  `

  let content = false
  if (loading) {
    content = (
      <div className="emptyPadWrapper">
        <Loading />
      </div>
    )
  } else if (!image) {
    content = (
      <div className="emptyPadWrapper">
        <Button onClick={onStartEditing} bgColor={COLORS.transparent} color={COLORS.dark3} borderColor={COLORS.dark3}>
          {t('insertSignature')}
        </Button>
      </div>
    )
  } else {
    content = (
      <div className="padWidthSignatureWrapper">
        <Image src={image} />
        <SignatureControls pad={signRef.current} isEditing={isEditing} onStartEditing={onStartEditing} />
      </div>
    )
  }

  const showPrefilledHint = !profileOnly && useSavedUserSignature && !!image

  return (
    <div css={style} className="signatureInput" ref={wrapperRef}>
      {content}

      <Separator weight={2} />

      {showPrefilledHint && (
        <SmallText size="xsmall" color={COLORS.dark4}>
          {t('signaturePrefilledFromProfile')}
        </SmallText>
      )}
      {showPrefilledHint && <Separator weight={1} />}

      {!profileOnly && (
        <FlexGrid gutter={0.5} justify="flex-start" align="flex-start">
          {initialName && (
            <FlexCol flex={1}>
              <SmallText size="xsmall" color={COLORS.dark3}>
                {t('common:name')}
              </SmallText>
              <Text>{initialName}</Text>
            </FlexCol>
          )}
          {initialDoc && (
            <FlexCol flex={1}>
              <SmallText size="xsmall" color={COLORS.dark3}>
                {t('document')}
              </SmallText>
              <Text>{initialDoc}</Text>
            </FlexCol>
          )}
        </FlexGrid>
      )}

      <Modal open={isEditing} width="44rem">
        <Form
          onSubmit={e => {
            e.preventDefault()
            if (isValid) confirmSignature(e)
          }}
          disabled={!isValid}
        >
          <Label>{t('includeSignatureInRectangle')}</Label>
          <div className="padWrapper">
            <SignaturePad
              height={192}
              options={{ penColor: COLORS.dark2, onBegin }}
              ref={signRef}
              redrawOnResize={true}
            />
            <SignatureControls pad={signRef.current} isEditing={isEditing} onStartEditing={onStartEditing} />
          </div>

          {!profileOnly && (
        <>
          <Separator weight={2} />

          <div className="userData">
            <FlexGrid gutter={0.5} justify="flex-start">
              <FlexCol flex={1}>
                <Label>{t('nameRequired')}</Label>
                <Input
                  key="signature-name"
                  value={effectiveName}
                  onChange={setNameAndRef}
                />
              </FlexCol>
              <FlexCol flex={1}>
                <Label>
                  {t('document')} {requiredDoc ? `(${t('common:required')})` : ''}
                </Label>
                <CnpjOrCpfInput
                  key="signature-doc"
                  value={effectiveDoc}
                  onChange={setDocAndRef}
                />
              </FlexCol>
            </FlexGrid>
          </div>

          <Separator weight={4} />
        </>
      )}

          <FlexGrid gutter={0.2} justify="flex-end">
            <Button transparent onClick={() => setEditing(false)}>
              {t('common:cancel')}
            </Button>
            <Button loading={loading} disabled={!isValid} type="button" onClick={confirmSignature}>
              {t('addSignature')}
            </Button>
          </FlexGrid>
        </Form>
      </Modal>
    </div>
  )
}

export default SignatureInput
